import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, usePageTitle } from '../App';
import { Button } from '../components/ui/button';
import { ArrowLeft, Eye, Bookmark, BookmarkCheck, Share2, Calendar, Users, Tag } from 'lucide-react';
import { toast } from 'sonner';
import WholePictureCard from '../components/WholePictureCard';
import { useTrackProgress } from '../hooks/useTrackProgress';

const categoryColors = {
  government: 'badge-government',
  phenomena: 'badge-phenomena',
  consciousness: 'badge-consciousness',
  physics: 'badge-physics',
  witnesses: 'badge-witnesses',
  creative: 'badge-creative'
};

export default function StoryDetailPage() {
  const { storyId } = useParams();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(null);

  // Track progress
  useTrackProgress('story', storyId);
  usePageTitle(story?.title);

  useEffect(() => {
    const fetchStory = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/stories/${storyId}`);
        setStory(res.data);
      } catch (error) {
        console.error('Error fetching story:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [storyId]);

  // Check if bookmarked
  useEffect(() => {
    if (!user) return;
    const checkBookmark = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/bookmarks`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        const bookmark = res.data.find(b => b.item_type === 'story' && b.item_id === storyId);
        if (bookmark) {
          setIsBookmarked(true);
          setBookmarkId(bookmark.bookmark_id);
        }
      } catch (error) {
        console.error('Error checking bookmark:', error);
      }
    };
    checkBookmark();
  }, [user, storyId]);

  const handleBookmark = async () => {
    if (!user) {
      toast.error('Please sign in to bookmark');
      return;
    }
    
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${bookmarkId}`, { headers, withCredentials: true });
        setIsBookmarked(false);
        setBookmarkId(null);
        toast.success('Bookmark removed');
      } else {
        const res = await axios.post(`${API}/bookmarks`, 
          { item_type: 'story', item_id: storyId },
          { headers, withCredentials: true }
        );
        setIsBookmarked(true);
        setBookmarkId(res.data.bookmark_id);
        toast.success('Bookmarked!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating bookmark');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: story?.title,
          text: story?.excerpt,
          url: url
        });
      } catch (e) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="skeleton h-8 w-32 mb-8" />
          <div className="skeleton h-96 rounded-sm mb-8" />
          <div className="skeleton h-4 w-3/4 mb-4" />
          <div className="skeleton h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/50">Story not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <article className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm uppercase tracking-wider">Back to Feed</span>
        </Link>

        {/* Header Image */}
        {story.image_url && (
          <div className="relative h-64 md:h-[500px] mb-8 overflow-hidden rounded-sm">
            <img src={story.image_url} alt={story.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            
            {/* Category */}
            <div className="absolute bottom-6 left-6">
              <span className={`px-3 py-1 text-xs font-mono uppercase tracking-wider rounded-sm ${categoryColors[story.category]}`}>
                {story.category}
              </span>
            </div>
          </div>
        )}

        {/* Title & Meta */}
        <header className="mb-8" data-testid="story-detail">
          <h1 className="font-heading text-3xl md:text-5xl font-bold mb-4 leading-tight">{story.title}</h1>
          
          {story.subtitle && (
            <p className="text-xl text-white/60 mb-6">{story.subtitle}</p>
          )}
          
          <div className="flex flex-wrap items-center gap-6 text-white/40 text-sm">
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {story.views || 0} views
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(story.created_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
            <Button 
              variant="ghost" 
              onClick={handleBookmark} 
              className="flex items-center gap-2 text-white/60 hover:text-white"
              data-testid="bookmark-btn"
            >
              {isBookmarked ? (
                <BookmarkCheck className="w-5 h-5 text-cyan-400" />
              ) : (
                <Bookmark className="w-5 h-5" />
              )}
              {isBookmarked ? 'Saved' : 'Save'}
            </Button>
            <WholePictureCard 
              type="story"
              data={{
                title: story.title,
                excerpt: story.excerpt,
                image: story.image_url,
                category: story.category
              }}
            />
          </div>
        </header>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none mb-12">
          {story.content.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="text-white/80 leading-relaxed mb-6">{paragraph}</p>
          ))}
        </div>

        {/* Tags */}
        {story.tags?.length > 0 && (
          <div className="mb-8 pb-8 border-b border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-white/40" />
              <span className="font-mono text-xs uppercase tracking-wider text-white/40">Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {story.tags.map(tag => (
                <span key={tag} className="px-3 py-1 text-sm bg-white/5 border border-white/10 rounded-sm">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Related People */}
        {story.related_people?.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-cyan-400" />
              <span className="font-mono text-xs uppercase tracking-wider text-white/40">Related People</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {story.related_people.map(personId => (
                <Link
                  key={personId}
                  to={`/people/${personId}`}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-sm hover:border-cyan-400/50 transition-colors"
                >
                  {personId.replace('person_', '').replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Source */}
        {story.source_url && (
          <div className="card-dark p-6">
            <span className="font-mono text-xs uppercase tracking-wider text-white/40 mb-2 block">Source</span>
            <a href={story.source_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline break-all">
              {story.source_url}
            </a>
          </div>
        )}
      </article>
    </div>
  );
}
